import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import {ReactComponent as Logo} from "@assets/images/logo.svg"
import {ReactComponent as Clock} from "@assets/images/big_clock.svg"
import BaseInputsGrid from '@reusables/BaseInputsGrid';
import BaseInput from '@reusables/BaseInput';
import BaseCheckbox from '@reusables/BaseCheckbox';
import BaseButton from '@reusables/BaseButton';
import BasePasswordRulesWrapper from '@reusables/passwords/BasePasswordRulesWrapper';
import BasePasswordInput from '@reusables/passwords/BasePasswordInput';
import { BaseLoadingBlocker } from '@reusables/blockers/BaseLoadingBlocker';

import { z } from 'zod';
import { refinements } from '@helpers/refinements';
import { requiredIfAnyFilled } from '@helpers/utils';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAcceptExistingUserInviteMutation, useAcceptNewUserInviteMutation, useGetInviteQuery } from '@redux/features/users/usersApi';
import { useAppDispatch } from '@redux/hooks';
import { authApi } from '@redux/features/auth/authApi';
import { authenticate, setUser } from "@redux/features/auth/authSlice";
import { companiesApi } from '@redux/features/companies/companiesApi';

import { Integration } from '@/types/general';
import LanguageChanger from '../LanguageChanger';

const Container = styled.div`
    width: 100%;
    min-height: 100dvh;
    background-color: rgb(252, 254, 255);
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
`

const FormContainer = styled.div`
    width: 100%;
    max-width: 600px;
    background-color: rgba(255, 255, 255, 1);
    box-shadow: rgba(152, 152, 166, 0.2)  0px 8px 24px;;
    padding: 40px 30px;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`

const formScheme = z.object({
    needsRegistration: z.boolean(),
    firstName: z.string().trim().min(2).refine(refinements.nonNumeric.refine, refinements.nonNumeric.message).optional(),
    last_name: z.string().trim().min(2).refine(refinements.nonNumeric.refine, refinements.nonNumeric.message).optional(),
    email: z.string().email().optional(),
    password: z.string().refine(refinements.passwordGeneral.refine).optional(),
    confirm_password: z.string().optional(),
    terms_and_conditions: z.boolean().refine(value => value, {
        params: {
            i18n: "signupPage.companyInformation.errors.termsAndConditions"
        }
    }).optional(),
    marketing_info: z.boolean().optional()
})
.superRefine((scheme, ctx) => {
    if (scheme.needsRegistration === true) {
        requiredIfAnyFilled(scheme, ["marketing_info"])
        .forEach(key => {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "general.validation.global.required"
                },
                path: [key]
            });
        })
    }
})
.superRefine((scheme, ctx) => {
    if (scheme.password !== scheme.confirm_password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: {
                i18n: "signupPage.yourProfile.errors.passwordMismatch"
            },
            path: ["confirm_password"]
        });
    }
})

type InvitationFormTyping = z.infer<typeof formScheme>;

export default function InvitationPage() {
    const { t } = useTranslation("", { keyPrefix: "invitationPage" });
    const params = useParams<{ id: string }>();

    const history = useHistory();
    const dispatch = useAppDispatch();

    const [isLoginLoading, setIsLoginLoading] = useState(false);

    const { data: invitationData, isLoading, isError, error } = useGetInviteQuery(params.id);
    const [acceptUserInvite, { isLoading: isAcceptingLoading }] = useAcceptExistingUserInviteMutation();
    const [createUserAccount, { isLoading: isCreateAccountLoading }] = useAcceptNewUserInviteMutation();

    const { control, handleSubmit, setValue, getValues} = useForm<InvitationFormTyping>({
        resolver: zodResolver(formScheme),
    })
    
    useEffect(() => {
        if (invitationData) {
            setValue("needsRegistration", invitationData?.needs_registration);
        }
    }, [invitationData, setValue])

    const onSubmit = handleSubmit(async (data) => {
        if (data.needsRegistration) {
            createUserAccount({ // Add 'await' here
                token_id: params.id.toString(),
                first_name: data.firstName ?? "",
                last_name: data.last_name ?? "",
                password: data.password ?? ""
            }).unwrap().then(async () => {
                    setIsLoginLoading(true);

                    let meQuery, companyQuery;

                    try {
                        const loginResponse = await dispatch(authApi.endpoints.login.initiate({
                            email: invitationData?.email ?? "",
                            password: data.password ?? "",
                            remember_me: false
                        })).unwrap(); // tokens
            
                        // Setting authentication tokens
                        dispatch(authenticate({
                            access: loginResponse.token,
                            refresh: loginResponse.refresh_token
                        }));
            
                        // Getting extended user information
                        meQuery = dispatch(authApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }));
                        const meData = await meQuery.unwrap();
            
                        companyQuery = dispatch(companiesApi.endpoints.getCompany.initiate(meData.company.id, { forceRefetch: true }));
                        const companyData = await companyQuery.unwrap();
            
                        dispatch(setUser({
                            ...meData,
                            company: companyData
                        }));
            
                        history.push("/dashboard/main");
                    } catch (err: any) {
                        toast.error(err.message);
                    } finally {
                        meQuery?.unsubscribe();
                        companyQuery?.unsubscribe();
                        setIsLoginLoading(false);
                    }
                }).catch((err) => {
                    console.log("Error", err);
                    toast.error(err.message);
                });
        } else {
            acceptUserInvite(params.id).unwrap().then(() => {
                history.push("/login");
            })
        }
    })

    if (isError) return (
        <Container>
            <FormContainer style={{ gap: "24px" }}>
                <div className='flex items-end justify-between'>
                    <div
                        className='text-[#5A5587] text-2xl font-bold'
                    >
                        {error.message}
                    </div>
                    <Clock />
                </div>
                <div
                    className='text-[#5A5587] text-sm font-normal'
                >
                    <p className='m-0'>{t("invalidInvitation.1")}</p>
                    <p className='m-0'>{t("invalidInvitation.2")}</p>
                </div>
            </FormContainer>
        </Container>
    );

    return (
        <Container>
            <div className='absolute top-0 right-0 p-10 z-10'>
                <LanguageChanger isList={false} />
            </div>
            <BaseLoadingBlocker active={isLoading || isLoginLoading}>
                <FormContainer>
                    <Logo />
                    <div className='space-y-4'>
                        <div className='text-2xl text-[#5A5587] font-bold'>{t("heading")}</div>
                        <div
                            className='text-[#5A5587] text-sm font-normal'
                        >
                            <Trans
                                i18nKey="invitationPage.subheading.default"
                                values={{ company: invitationData?.company_name }}
                                components={{ bold: <span className="font-semibold" /> }}
                            />
                            
                            {invitationData?.needs_registration 
                                ? 
                                <Trans
                                    i18nKey="invitationPage.subheading.nonExistingUser"
                                    values={{ email: invitationData?.email }}
                                    components={{ bold: <span className="font-semibold" /> }}
                                />
                                : t("subheading.existingUser")
                            }
                        </div>
                    </div>
                    <form className='space-y-8' onSubmit={onSubmit}>
                        <>
                            {invitationData?.needs_registration && 
                                <>
                                    <div className='flex flex-col gap-6'>
                                        <BaseInputsGrid cols={{sm: 2, xs:1}} gap={24}>
                                            <Controller
                                                control={control}
                                                name="firstName"
                                                render={
                                                    ({ field, fieldState }) => (
                                                        <BaseInput
                                                            {...field}
                                                            error={fieldState.error}
                                                            label={t("fields.firstName")}
                                                        />
                                                    )
                                                }
                                            />

                                            <Controller
                                                control={control}
                                                name="last_name"
                                                render={
                                                    ({ field, fieldState }) => (
                                                        <BaseInput
                                                            {...field}
                                                            error={fieldState.error}
                                                            label={t("fields.lastName")}
                                                        />
                                                    )
                                                }
                                            />
                                            <Controller
                                                control={control}
                                                name="password"
                                                render={
                                                    ({ field, fieldState }) => (
                                                        <BasePasswordRulesWrapper value={field.value ?? ""}>
                                                            <BasePasswordInput
                                                                field={field}
                                                                error={fieldState.error}
                                                                label={t("fields.password")}
                                                                showPasswordCondition={!!getValues("password")}
                                                                hideErrors
                                                            />
                                                        </BasePasswordRulesWrapper>
                                                    )
                                                }
                                            />

                                            <Controller
                                                control={control}
                                                name="confirm_password"
                                                render={
                                                    ({ field, fieldState }) => (
                                                        <BasePasswordInput
                                                            field={field}
                                                            error={fieldState.error}
                                                            label={t("fields.confirmPassword")}
                                                            showPasswordCondition={!!getValues("confirm_password")}
                                                        />
                                                    )
                                                }
                                            />
                                        </BaseInputsGrid>
                                    </div>
                                
                                    <div className="space-y-[24px]">
                                        <Controller
                                            control={control}
                                            name="terms_and_conditions"
                                            render={({ field, fieldState }) => (
                                                <BaseCheckbox
                                                    {...field}
                                                    {...fieldState}
                                                    label={
                                                        <div className='text-sm text-[#5A5587] '>
                                                            <Trans i18nKey={"checkboxes.terms"}
                                                                    t={t}
                                                                    components={{
                                                                        termsLink: <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer"
                                                                                        className={"text-decoration-underline px-1"} />,
                                                                        policyLink: <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer"
                                                                                            className={"text-decoration-underline px-1"} />
                                                                    }}
                                                            />
                                                            .
                                                        </div>
                                                    }
                                                />
                                            )}
                                        />
                                        <Controller
                                            control={control}
                                            name="marketing_info"
                                            render={({ field, fieldState }) => (
                                                <BaseCheckbox
                                                    {...field}
                                                    {...fieldState}
                                                    label={<span className="text-sm text-[#5A5587] ">{t("checkboxes.marketingInfo")}</span>}
                                                />
                                            )}
                                        />
                                    </div>
                                </>
                            }

                            <BaseButton
                                type="submit"
                                loading={isAcceptingLoading || isCreateAccountLoading}
                                text={invitationData?.needs_registration ? t("buttons.createAccount") : t("buttons.accept")}
                                size="md"
                                className="w-full justify-self-center"
                                style={{ backgroundImage: "linear-gradient(90.89deg, #2B2A35 0.67%, #6959FC 99.24%) 1" }}
                            />
                        </>
                    </form>
                </FormContainer>
            </BaseLoadingBlocker>
        </Container>
    );
}
